// src/components/practice/MarkSheet.jsx
//
// The AI feedback, rendered as a mark sheet (lesson-3c-marksheet). ONE component,
// used by both the live tiers (EssayFeedbackTiers) and the history view
// (MyEssaysPanel FeedbackView), so a student sees the same thing in both places and
// the two cannot drift apart.
//
// RENDERING ONLY. The grading contract -- gradePrompt.js, validateGradeResult,
// essayAttemptHelpers -- is untouched by this slice. Every value here comes from the
// record; nothing is derived that the server did not send.
//
// The one thing this component deliberately does NOT do: the persisted `criteria`
// carry a name, a comment and evidence, and NO per-criterion pass/fail. So there are
// no ticks, no warning triangles and no colour-coding on the criteria cards. A status
// glyph there would be a verdict the model never gave.
//
// Styling note: it uses the book-* namespace, which aliases the same semantic tokens
// as midnight-* (index.css), so it renders correctly inside the Account page too
// without a per-namespace class map.
import React, { useState } from 'react';
import { confidencePlain, gradingMaterialLines } from './aiFeedbackMessages.js';
import { markSheetView, markSheetCriteria, markSheetMoves } from './markSheetView.js';
import { hasUnreadableFeedback, UNAVAILABLE_FEEDBACK_NOTICE } from '../../utils/feedbackText.js';
import '../reader/lesson.css';
import { withoutPendingReviewNotes } from '../../../functions/src/ai/reviewNotice.js';

export default function MarkSheet({ fb: rawFeedback, children }) {
    const fb = withoutPendingReviewNotes(rawFeedback);
    if (!fb) return null;
    const view = markSheetView(fb);
    const criteria = markSheetCriteria(fb);
    const moves = markSheetMoves(fb);
    const conf = confidencePlain(fb.confidence);
    const materials = gradingMaterialLines(fb.gradingMaterials);

    return (
        <div className="space-y-4">
            {/* ---- Header: the grade, then the marks behind it ---- */}
            <div className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                {fb.assessmentLabel && <p className="font-display text-book-text text-sm font-semibold mb-2">{fb.assessmentLabel}</p>}
                {fb.assessmentNote && <p className="font-serif text-book-muted text-sm mb-3">{fb.assessmentNote}</p>}
                {fb.assessmentProfileId && <p className="font-serif text-book-muted text-xs mb-2">Task-specific formative feedback. No qualification grade is inferred.</p>}
                {view.grade && (
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-book-accent text-book-pageDark font-display text-lg font-extrabold leading-none">
                            {view.grade}
                        </span>
                        {view.gradeSub && (
                            <span className="font-serif italic text-book-muted text-xs">{view.gradeSub}</span>
                        )}
                    </div>
                )}

                {/* AO bars -- marks mode only. A band-only record shows none rather
                    than a bar drawn from a number the server never sent. */}
                {view.bars.length > 0 && (
                    <div className="mt-4 space-y-2.5">
                        {view.bars.map((b) => (
                            <div key={b.key}>
                                <div className="flex items-baseline justify-between gap-3 mb-1">
                                    <span className="font-display text-book-text text-xs font-semibold">{b.label}</span>
                                    <span className="font-display text-book-muted text-xs">
                                        {b.mark}/{b.maxMark}
                                        {/* The tint marks the tag; the TEXT stays in normal ink.
                                            Accent on its own 10% accent tint measured 4.36:1 in
                                            Dark -- under AA for small text. */}
                                        <span className="ml-2 px-1.5 py-0.5 rounded-full bg-book-accent/10 text-book-text font-semibold">Level {b.level}</span>
                                    </span>
                                </div>
                                <div className="h-1.5 rounded-full bg-book-accent/10 overflow-hidden">
                                    <div className="h-full bg-book-accent" style={{ width: b.pct + '%' }} />
                                </div>
                            </div>
                        ))}
                        {view.totalMark != null && (
                            <div className="pt-1 font-display text-book-text text-xs font-semibold">
                                Total {view.totalMark}/{view.totalMax}
                            </div>
                        )}
                    </div>
                )}

                {/* profileNote: a --warn-tinted aside, because it is a caveat about the
                    shape of the draft, not a mark. */}
                {fb.profileNote && (
                    <p className="mt-3 rounded-[var(--radius-card)] border border-book-warn/40 bg-book-warn/10 p-3 font-serif text-book-text text-xs leading-relaxed">
                        {fb.profileNote}
                    </p>
                )}

                <p className="mt-3 font-serif italic text-book-muted text-xs">
                    Confidence: {conf.label} &ndash; {conf.note}.
                </p>
            </div>

            {/* ---- Criteria as cards: name, comment, and the evidence quoted ---- */}
            {materials.length > 0 && (
                <details className="rounded-[var(--radius-card)] border border-book-line bg-book-page p-4">
                    <summary className="cursor-pointer font-display text-book-text text-sm font-semibold">Pack materials supplied</summary>
                    <div className="mt-3 space-y-2 font-serif text-book-muted text-xs leading-relaxed">
                        <p>These materials were attached for this assessment. This does not mean every source or the whole pack was checked.</p>
                        {materials.map((line, i) => <p key={i} className="break-words">{line}</p>)}
                    </div>
                </details>
            )}
            {hasUnreadableFeedback(fb) && (
                <p role="status" className="font-serif text-book-muted text-sm leading-relaxed">
                    {UNAVAILABLE_FEEDBACK_NOTICE}
                </p>
            )}
            {criteria.length > 0 && (
                <div className="space-y-3">
                    {criteria.map((c, i) => (
                        <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                            {c.name && <div className="font-display text-book-accent text-xs mb-1.5 font-semibold">{c.name}</div>}
                            {c.comment && <p className="font-serif text-book-text text-sm leading-relaxed">{c.comment}</p>}
                            {c.evidence && (
                                <div className="mt-2.5 border-l-2 border-book-accent/40 pl-3">
                                    <div className="font-display text-book-muted text-[11px] font-semibold mb-0.5">In your draft</div>
                                    <p className="font-accent italic text-book-text text-sm leading-relaxed">{c.evidence}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* ---- The moves, as a checklist ---- */}
            {moves.length > 0 && <NextMoves moves={moves} />}

            {/* Caller-supplied extras (the tutor nudge, Copy feedback) sit here, before
                the disclaimer, so the server-stamped text stays last on the page. */}
            {children}

            {fb.disclaimer && (
                <p className="font-serif italic text-book-muted text-xs border-t border-book-line pt-3">{fb.disclaimer}</p>
            )}
        </div>
    );
}

// "Your next N moves" -- the improvements as tickable rows.
//
// The ticks are LOCAL STATE ONLY and deliberately so: nothing is persisted by this
// slice, and a checkbox that silently forgets itself on reload is better than a new
// write path bolted onto a read-only history view. They exist to let a student work
// through the list in one sitting.
function NextMoves({ moves }) {
    const [checked, setChecked] = useState(() => new Set());
    const toggle = (i) => setChecked((prev) => {
        const next = new Set(prev);
        if (next.has(i)) next.delete(i); else next.add(i);
        return next;
    });

    return (
        <div className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
            <div className="font-display text-book-accent text-xs mb-2.5 font-semibold">
                Your next {moves.length === 1 ? 'move' : moves.length + ' moves'}
            </div>
            <ul className="space-y-2">
                {moves.map((s, i) => {
                    const on = checked.has(i);
                    return (
                        <li key={i}>
                            <button type="button" onClick={() => toggle(i)} aria-pressed={on}
                                className="w-full text-left flex items-start gap-3 group">
                                <span className={'mt-0.5 flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors '
                                    + (on ? 'bg-book-accent border-book-accent text-book-pageDark' : 'border-book-accent/50 text-transparent group-hover:border-book-accent')}>
                                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </span>
                                <span className={'font-serif text-sm leading-relaxed ' + (on ? 'text-book-muted line-through' : 'text-book-text')}>
                                    {s}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
            <p className="font-serif italic text-book-muted text-xs mt-3">
                Redraft with these in hand, then mark it again to see the change.
            </p>
        </div>
    );
}
